import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { Center, Table } from "@mantine/core"
import { TFunction } from "i18next";

import { Checkbox, CommentsModal, Link, PublicLinkModal } from "components";
import { AuthorityConstants, RouteConstants, ServiceConstants } from "constant";
import { CardService } from "services";
import { CardResponse } from "types";
import { AppUtils, UserUtils } from "utils";

import { IRootState } from "config/redux";
import { IconLink } from "@tabler/icons-react";

const { ROLE_CC_R, ROLE_CC_C, ROLE_USR_CMT } = AuthorityConstants;
const { ROUTE_CARDS } = RouteConstants;
const { REQUEST_PENDING, REQUEST_CARDS_TRANSFER_TO_CRM_ID, REQUEST_CARDS_LINK_CREATE_ID } = ServiceConstants;

interface CardListItemProps {
  item: CardResponse;
  t: TFunction;
}

const CardListItem = ({ item, t }: CardListItemProps) => {
  const serviceObject = useSelector((state: IRootState) => state.service.object[REQUEST_CARDS_TRANSFER_TO_CRM_ID + item.id]);
  const [inCrm, setInCrm] = useState<boolean>(item.crm);
  const [commentsVisible, setCommentsVisible] = useState<boolean>(false);
  const [linkVisible, setLinkVisible] = useState<boolean>(false);
  const [numberOfComments, setNumberOfComments] = useState<number>(item.user.numberOfComments);

  useEffect(() => {
    AppUtils.resolveServiceObject(serviceObject, REQUEST_CARDS_TRANSFER_TO_CRM_ID + item.id, setInCrm);
  }, [serviceObject]);

  const inCrmChange = useCallback(() => CardService.cardsTransferToCrm(item.id), []);
  const isLoading = useMemo(() => serviceObject && serviceObject.status === REQUEST_PENDING, [serviceObject]);

  return (
    <Table.Tr key={item.id}>
      <Table.Td>
        {UserUtils.hasAuthority(ROLE_CC_R) ?
          <Link to={`${ROUTE_CARDS}/${item.id}`}>
            {item.user.fullName}
          </Link>
          : item.user.fullName}
      </Table.Td>
      <Table.Td visibleFrom={'md'}>{item.gender && item.gender.charAt(0)}</Table.Td>
      <Table.Td>{item.user.countryCode + ' ' + item.user.phoneNumber}</Table.Td>
      <Table.Td visibleFrom={'md'}>{AppUtils.composeAddress(item)}</Table.Td>
      <Table.Td>{item.user.source && t('source.options.' + item.user.source)}</Table.Td>
      <Table.Td>{item.requestedAt && t('format.date', { date: new Date(item.requestedAt) })}</Table.Td>
      <Table.Td ta={'center'}>
        {UserUtils.hasAuthority(ROLE_USR_CMT) ?
          <>
            <Link onClick={() => setCommentsVisible(true)}>{numberOfComments}</Link>
            <CommentsModal
              opened={commentsVisible}
              onClose={() => setCommentsVisible(false)}
              title={t('card.comment.title')}
              userId={item.user.id}
              incrementNumberOf={() => setNumberOfComments(old => old + 1)}
            />
          </>
          : numberOfComments
        }
      </Table.Td>
      {UserUtils.hasAuthority(ROLE_CC_R) &&
        <Table.Td ta={'center'}>
          <Link onClick={() => setLinkVisible(true)}><IconLink size={16} stroke={1.5} /></Link>
          <PublicLinkModal
            opened={linkVisible}
            onClose={() => setLinkVisible(false)}
            create={() => CardService.cardLinkCreate(item.id)}
            requestId={REQUEST_CARDS_LINK_CREATE_ID}
            t={t}
            link={item.link}
            authority={ROLE_CC_C}
            object={'card'}
          />
        </Table.Td>
      }
      <Table.Td visibleFrom={'md'}>
        <Center>
          <Checkbox
            checked={inCrm}
            onChange={inCrmChange}
            loading={isLoading}
            readOnly={!UserUtils.hasAuthority(AuthorityConstants.ROLE_CC_C)}
          />
        </Center>
      </Table.Td>
    </Table.Tr>
  )
}

export default CardListItem;